import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";
import * as actions from "../../../../../redux/actions/dataAnalyst";
import {
  getArtistChartStats,
  getArtistStats,
} from "../../../../../redux/actions";

const Searchbar = ({
  searchPlaceholder,
  sendingThrough,
  fetchTrackActivities,
  fetchTrackStats,
  type,
  period,
  isArtistData,
  forMap,
  onSelectTrack = () => {},
  source,
  trackType,
}) => {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadMilestones, setLoadMilestones] = useState(false);
  const [loadArtistData, setLoadArtistData] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSelected, setIsSelected] = useState(false);
  const { artistInfo } = useSelector((state) => state.artistDetails);
  const searchData = useSelector((state) => state.dataanalyst);
  const { hasActiveSubscription } = useSelector((state) => state.billing);
  const { allTracks } = searchData;
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debounce logic
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim()) {
        let queryParams = {
          query: query,
          type: trackType || "track",
        };
        if(hasActiveSubscription){
          dispatch(actions.searchTrack(toast, queryParams, setIsLoading));
          if (allTracks?.data?.length) {
            setResults(allTracks.data);
          } else {
            setResults([]);
        }
        }
      }
    }, 400),
    [dispatch, query, hasActiveSubscription]
  );

  const handleItemClick = (data) => {
    onSelectTrack(data.id);
    if (fetchTrackActivities) {
      // Mark the appropriate search-query in redux so lists can switch to track-scoped API
      if (sendingThrough === 'MILESTONE') {
        dispatch(actions.searchQuery(data, 'milestoneSearchQuery'));
      } else if (type === 'RADIO_MILESTONE' || sendingThrough === 'RADIO_MILESTONE') {
        // Radio uses the generic `searchQuery` key elsewhere (radio page expects this)
        dispatch(actions.searchQuery(data, 'searchQuery'));
      } else {
        dispatch(actions.searchQuery(data, 'playlistSearchQuery'));
      }
      const queryParam = {
        spotify_track_id: data.id,
        source: source,
      };
      dispatch(actions.getTrackActivities(toast, queryParam, type));
    } else if (fetchTrackStats) {
      dispatch(actions.searchQuery(data, 'searchQuery'));
      const queryParam = {
        spotify_track_id: data.id,
        type: period,
        source: source === "all" || source == undefined ? "spotify" : source,
      };
      dispatch(actions.getTrackStats(toast, queryParam, isArtistData));
      dispatch(actions.getTrackChartsStats(toast, queryParam, isArtistData));
      // }
    } else if (forMap) {
      const queryParam = {
        spotify_track_id: data.id,
        type: "all",
        source: "radio",
      };
      dispatch(actions.getTrackStatsForMap(toast, queryParam));
    }
    setResults([]);
    setQuery(data.name);
    setIsSelected(true);
  };

  useEffect(() => {
    // Cleanup function to clear search query when component unmounts
    return () => {
      dispatch(actions.clearSearchQuery());
    };
  }, [dispatch]);

  return (
    <div ref={wrapperRef} className="search-wrapper relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          const value = e.target.value;
          setQuery(value);
          if (value.trim()) {
            debouncedSearch(value);
          } else {
            setResults([]);
          }
          setIsSelected(false);
        }}
        placeholder={searchPlaceholder || "Search..."}
        className="pl-8 pr-4 py-1 w-full bg-transparent text-white border border-white rounded-md focus:outline-none"
      />
      <svg
        className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <div>
        {allTracks?.length > 0 && query.trim() !== "" && !isSelected && (
          <ul className="absolute top-full mt-1 w-full bg-white text-black border border-gray-300 rounded shadow z-10">
            {allTracks.map((track) => (
              <li
                key={track.id}
                onClick={() => handleItemClick(track)}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
              >
                {track.name}
              </li>
            ))}
          </ul>
        )}
      </div>
      {query && (
        <button
          onClick={() => {
            // Clear both local and redux state
            // Clear search queries used by Milestones/Playlists
            dispatch(actions.clearSearchQuery());
            // explicitly clear track-scoped search queries so lists revert to default API
            dispatch(actions.searchQuery({}, 'milestoneSearchQuery'));
            dispatch(actions.searchQuery({}, 'playlistSearchQuery'));
            // reset any track-activity failure flag
            dispatch(actions.trackActivitiesFails(""));
            onSelectTrack("");
            setQuery("");
            setResults([]);
            setIsSelected(false);

            // Reset relevant data based on type
            if (fetchTrackActivities) {
              if (type === "RADIO_MILESTONE") {
                dispatch(
                  actions.getRadioMilestones(toast, {}, setLoadMilestones)
                );
              } else {
                dispatch(
                  actions.getMilestoneActivities(
                    toast,
                    { source },
                    type,
                    setLoadMilestones
                  )
                );
              }
            } else if (fetchTrackStats) {
              const queryParams = {
                type: period,
                source: source === "all" ? "spotify" : source,
              };
              dispatch(getArtistStats(toast, queryParams, setLoadArtistData));
              dispatch(getArtistChartStats(toast, queryParams));
            }
          }}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default Searchbar;
